import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HiHome, HiUsers, HiOutlineChartBar } from 'react-icons/hi2';
import { HiOutlineViewGridAdd } from 'react-icons/hi';
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp, MdOutlineProductionQuantityLimits, MdEmojiEvents } from "react-icons/md";
import { IoNotificationsCircleOutline } from "react-icons/io5";
import { LuShoppingCart } from "react-icons/lu";
import { motion, AnimatePresence } from 'framer-motion';

const sidebarItems = [
  {
    label: 'Home',
    icon: HiHome,
    path: '/',
    type: 'button'
  },
  {
    label: 'Dashboard',
    icon: HiOutlineChartBar,
    path: '/admin',
    type: 'button'
  },
  {
    label: 'Attività',
    icon: IoNotificationsCircleOutline,
    path: '/admin?component=activity',
    type: 'button'
  },
  {
    label: 'Tabelle',
    icon: HiOutlineViewGridAdd,
    type: 'dropdown',
    subItems: [
      {
        label: 'Utenti',
        icon: HiUsers,
        path: '/admin?component=users',
      },
      {
        label: 'Ordini',
        icon: LuShoppingCart,
        path: '/admin/?component=orders',
      },
      {
        label: 'Prodotti',
        icon: MdOutlineProductionQuantityLimits,
        path: '/admin/?component=products',
      },
      {
        label: 'Eventi',
        icon: MdEmojiEvents,
        path: '/admin/?component=events',
      }
    ],
  },
];

export default function AdminSidebar() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  
  // Animation variants
  const sidebarVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.4,
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3 }
    },
    hover: { 
      backgroundColor: "rgba(255, 255, 255, 0.05)", 
      transition: { duration: 0.2 } 
    },
    tap: { scale: 0.98 }
  };
  
  const iconVariants = {
    hover: { 
      rotate: [0, -10, 10, -5, 0],
      transition: { duration: 0.5 }
    }
  };
  
  const dropdownVariants = {
    hidden: { 
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren"
      }
    },
    visible: { 
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.05
      }
    }
  };
  
  const subItemVariants = {
    hidden: { opacity: 0, x: -5 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3 }
    }
  };

  // Set initial dropdown state based on current path
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    for (const item of sidebarItems) {
      if (item.type === 'dropdown') {
        const isActive = item.subItems.some(subItem => 
          currentPath.includes(new URL(subItem.path, window.location.origin).pathname)
        );
        
        if (isActive) {
          setOpenDropdown(item.label);
          break;
        }
      }
    }
  }, [location]);

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  // Check if a link is active
  const isActiveLink = (path) => {
    const currentPath = location.pathname + location.search;
    // For dropdown items, match the component parameter
    if (path.includes('?component=')) {
      return currentPath.includes(path);
    }
    // For main paths like '/admin', match exactly
    return path === currentPath;
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={sidebarVariants}
      className="py-6 h-[95vh] px-4"
    >
      <aside className="flex top-0 left-0 z-40 rounded-lg h-full transition-transform -translate-x-full sm:translate-x-0 bg-[#121212]" aria-label="Sidebar">
        <div className="h-full px-3 py-4 overflow-y-auto w-full">
          <motion.ul 
            variants={sidebarVariants}
            className="space-y-2 font-medium w-full flex items-start flex-col mr-12"
          >
            {sidebarItems.map((item) => (
              <motion.li 
                key={item.label} 
                variants={itemVariants}
                className="w-full"
              >
                {item.type === 'dropdown' ? (
                  <>
                    <motion.button
                      variants={itemVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => toggleDropdown(item.label)}
                      className={`flex items-center flex-row justify-between p-3 w-full rounded-lg group space-x-3 ${
                        openDropdown === item.label 
                          ? 'bg-[#1d1d1d] text-white' 
                          : 'text-gray-200 hover:text-white'
                      }`}
                    >
                      <div className="inline-flex items-center">
                        <motion.div
                          variants={iconVariants}
                          whileHover="hover"
                          className={`flex-shrink-0 ${openDropdown === item.label ? 'text-white' : 'text-gray-300'}`}
                        >
                          <item.icon className="w-5 h-5" />
                        </motion.div>
                        <span className="flex-1 ms-3 whitespace-nowrap">{item.label}</span>
                      </div>
                      <motion.span
                        animate={{ 
                          rotate: openDropdown === item.label ? 180 : 0 
                        }}
                        transition={{ duration: 0.3 }}
                        className="ml-auto text-gray-400"
                      >
                        <MdOutlineKeyboardArrowDown />
                      </motion.span>
                    </motion.button>

                    <AnimatePresence>
                      {openDropdown === item.label && (
                        <motion.ul
                          variants={dropdownVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="ml-4 space-y-1 overflow-hidden mt-2"
                        >
                          {item.subItems.map((subItem) => {
                            const isActive = isActiveLink(subItem.path);
                            return (
                              <motion.li 
                                key={subItem.label}
                                variants={subItemVariants}
                              >
                                <motion.div
                                  whileHover="hover"
                                  whileTap="tap"
                                  variants={itemVariants}
                                >
                                  <Link 
                                    to={subItem.path} 
                                    className={`flex items-center p-3 rounded-lg group ${
                                      isActive 
                                        ? 'bg-[#232323] text-white' 
                                        : 'text-gray-300 hover:bg-[#1d1d1d] hover:text-white'
                                    }`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${
                                      isActive ? 'bg-blue-500' : 'bg-transparent'
                                    }`}></span>
                                    <subItem.icon className={`w-4 h-4 ${
                                      isActive ? 'text-blue-500' : 'text-gray-400'
                                    }`} />
                                    <span className={`ml-3 whitespace-nowrap ${
                                      isActive ? 'font-medium' : ''
                                    }`}>
                                      {subItem.label}
                                    </span>
                                  </Link>
                                </motion.div>
                              </motion.li>
                            );
                          })}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <motion.div
                    whileHover="hover"
                    whileTap="tap"
                    variants={itemVariants}
                  >
                    <Link 
                      to={item.path} 
                      className={`flex items-center p-3 rounded-lg ${
                        isActiveLink(item.path) 
                          ? 'bg-[#1d1d1d] text-white' 
                          : 'text-gray-200 hover:bg-[#1d1d1d] hover:text-white'
                      }`}
                    >
                      <motion.div
                        variants={iconVariants}
                        whileHover="hover"
                        className={isActiveLink(item.path) ? 'text-blue-500' : 'text-gray-400'}
                      >
                        <item.icon className="w-5 h-5" />
                      </motion.div>
                      <span className={`flex-1 ms-3 whitespace-nowrap ${
                        isActiveLink(item.path) ? 'font-medium' : ''
                      }`}>
                        {item.label}
                      </span>
                      {isActiveLink(item.path) && (
                        <motion.span
                          layoutId="activeIndicator"
                          className="w-1.5 h-5 bg-blue-500 rounded-full ml-2"
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                )}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </aside>
    </motion.div>
  );
}